/**
 * V0.3.11 — Context-aware live help.
 *
 * Replaces the static HELP_PROMPTS dicts in HelpModal + PipView with a
 * single methodology-aware function that reacts to what is MISSING or
 * UNDERDEVELOPED in the live conversation.
 *
 * Pure client-side JS. No LLM call. Cheap to run on every render.
 * Returns section-shaped output so both surfaces (HelpModal rich UI +
 * PipView flat list) can render from the same source of truth.
 *
 * Preserves the V0.3.10 pain-point-preservation ethos: help is a nudge,
 * not a script. Short, natural, one prompt per reason.
 */

const DISCOVERY_KEYS = [
  "pain_points",
  "goals",
  "previous_attempts",
  "cost_of_inaction",
  "decision_maker",
  "financial_situation",
];

// --- Signal detectors on the raw transcript (recent 12 lines) --------------
const OBJECTION_PRICE_RE = /\b(too expensive|too much|can'?t afford|cost too much|price|out of budget|more than (i|we) expected)\b/i;
const OBJECTION_TIME_RE = /\b(not (a )?good time|not the right time|maybe (later|next (month|quarter|year))|too busy right now|not right now|hold off)\b/i;
const OBJECTION_SPOUSE_RE = /\b(talk|speak|check|ask)\s+(with\s+|to\s+)?(my\s+)?(husband|wife|partner|spouse|other half)\b/i;
const OBJECTION_STALL_RE = /\b(think about it|need to think|let me think|sleep on it|get back to you|i'?ll let you know)\b/i;
const OBJECTION_TRUST_RE = /\b(paid people before|been burned|last time|last person|previous (agency|provider|marketer))\b/i;

// Shallow-pain detector: item exists but "text" is skimpy or lacks impact language
const IMPACT_WORDS_RE = /\b(cost|lose|losing|lost|risk|stress|morale|revenue|staff|pressure|payroll|burnout|hours|time|profit)\b/i;

// -- Helpers ---------------------------------------------------------------
const isThin = (item) => {
  const t = (item?.text || "").trim();
  return t.split(/\s+/).length < 6;
};

const hasFailureWhy = (item) => {
  const t = ((item?.text || "") + " " + (item?.quote || "")).toLowerCase();
  return /(didn'?t work|failed|no visibility|no tracking|inconsistent|nothing worked|overpromised|didn'?t deliver|no results|little|no return)/i.test(t);
};

const topByMentions = (items) => {
  if (!items?.length) return null;
  return [...items].sort(
    (a, b) => (b.mentions || 1) - (a.mentions || 1) || (b.text?.length || 0) - (a.text?.length || 0),
  )[0];
};

const short = (s, n = 90) => {
  if (!s) return "";
  return s.length <= n ? s : s.slice(0, n - 1).trimEnd() + "…";
};

// --- Phase-specific builders ---------------------------------------------
function buildUpfrontSections() {
  return [
    {
      title: "Set the frame",
      items: [
        "Audio + camera check",
        "Confirm the time available (\"45 minutes OK?\")",
        "State the agenda in one sentence",
      ],
    },
  ];
}

function buildDiscoverySections({ state, mode }) {
  const s = state || {};
  const nudges = [];

  // "What's missing or underdeveloped" — one short prompt per gap.
  const painsFilled = (s.pain_points || []).length > 0;
  const goalsFilled = (s.goals || []).length > 0;
  const impactFilled = (s.cost_of_inaction || []).length > 0;
  const attemptsFilled = (s.previous_attempts || []).length > 0;
  const decisionFilled = (s.decision_maker || []).length > 0;
  const financialFilled = (s.financial_situation || []).length > 0;

  const painPrompt = { key: "pain", label: painsFilled ? "Deepen the pain" : "Get the pain", items: [] };
  if (!painsFilled) {
    painPrompt.items.push("\"What's the biggest thing you're trying to fix right now?\"");
  } else {
    const shallow = (s.pain_points || []).some(isThin);
    if (shallow) painPrompt.items.push("\"Tell me more about that.\"");
    painPrompt.items.push("\"Can you give me a specific example?\"");
    painPrompt.items.push("\"How long has that been going on?\"");
  }

  const goalPrompt = { key: "goals", label: goalsFilled ? "Sharpen the goal" : "Get the goal", items: [] };
  if (!goalsFilled) {
    goalPrompt.items.push("\"What would the ideal situation look like?\"");
    goalPrompt.items.push("\"What does success look like in 90 days?\"");
  } else {
    goalPrompt.items.push("\"What does it unlock for you personally?\"");
    goalPrompt.items.push("\"By when do you want this solved?\"");
  }

  const impactPrompt = { key: "impact", label: "Impact / Cost of Inaction", items: [] };
  if (!impactFilled) {
    impactPrompt.items.push("\"How is that impacting you?\"");
    impactPrompt.items.push("\"What happens if another 6 months go by and nothing changes?\"");
  } else {
    impactPrompt.items.push("Loop the cost back in their own words");
  }

  const attemptsPrompt = { key: "attempts", label: attemptsFilled ? "Why attempts failed" : "Previous attempts", items: [] };
  if (!attemptsFilled) {
    attemptsPrompt.items.push("\"What have you tried to solve that?\"");
  } else {
    const attemptsWithoutWhy = (s.previous_attempts || []).filter((a) => !hasFailureWhy(a));
    if (attemptsWithoutWhy.length > 0) {
      attemptsPrompt.items.push("\"Why do you think that didn't work?\"");
      attemptsPrompt.items.push("\"What was missing from what they gave you?\"");
    } else {
      attemptsPrompt.items.push("\"Have you tried anything else?\"");
    }
  }

  const decisionPrompt = { key: "decision", label: "Decision process", items: [] };
  if (!decisionFilled) {
    decisionPrompt.items.push("\"How are you going to make this decision?\"");
    decisionPrompt.items.push("\"Anyone else involved in this?\"");
  } else {
    decisionPrompt.items.push("\"If the right plan was in front of you today, could you decide?\"");
  }

  const financialPrompt = { key: "financial", label: "Financial", items: [] };
  if (!financialFilled) {
    financialPrompt.items.push("\"What's the budget range you had in mind?\"");
    financialPrompt.items.push("\"How much has this cost you already?\"");
  } else {
    financialPrompt.items.push("\"Would you invest to solve this if it worked?\"");
  }

  const roadblockPrompt = {
    key: "roadblocks",
    label: "Roadblocks & why now",
    items: [
      "\"What's stopping you from achieving this?\"",
      "\"Why solve this now rather than in 6 months?\"",
    ],
  };

  // Methodology ordering.
  // B2B: Pain / Current → Impact → Goals → deeper discovery.
  // B2C: Goals / Desired → Current / Pain → deeper discovery.
  const b2b = [painPrompt, impactPrompt, goalPrompt, attemptsPrompt, roadblockPrompt, decisionPrompt, financialPrompt];
  const b2c = [goalPrompt, painPrompt, impactPrompt, attemptsPrompt, roadblockPrompt, decisionPrompt, financialPrompt];
  const ordered = (mode === "b2c" ? b2c : b2b).filter((p) => p.items.length > 0);

  ordered.forEach((p) => nudges.push({ title: p.label, items: p.items.slice(0, 3) }));
  return nudges;
}

function buildPrescriptionSections({ state }) {
  const s = state || {};
  const topPain = topByMentions(s.pain_points);
  const topGoal = topByMentions(s.goals);
  const topRoadblock =
    (s.pain_points || []).find((p) => /can'?t|stuck|struggle|nervous|worried|blocked/i.test(p.text || "")) || null;

  const sections = [];

  // Recap section — surfaces the strongest captured evidence in the
  // prospect's own words when available (quote wins over text).
  const recapItems = [];
  if (topGoal) {
    recapItems.push(
      `Desired: "${short(topGoal.quote || topGoal.text)}"`,
    );
  } else {
    recapItems.push("Recap their desired state in one line");
  }
  if (topPain) {
    recapItems.push(
      `Current pain: "${short(topPain.quote || topPain.text)}"`,
    );
  } else {
    recapItems.push("Recap their current state in one line");
  }
  if (topRoadblock && topRoadblock !== topPain) {
    recapItems.push(
      `Roadblock: "${short(topRoadblock.quote || topRoadblock.text)}"`,
    );
  }
  sections.push({ title: "Recap the gap", items: recapItems });

  // Problem → Solution → Benefit framing.
  const psbItems = [
    "\"It sounds like what you need is [solution], so that [benefit].\"",
    "Tie each pain to a specific piece of the solution",
    "Reference their goals by name",
  ];
  if (topPain) psbItems.push(`Quote them back: "${short(topPain.quote || topPain.text, 60)}"`);
  sections.push({ title: "Problem → Solution → Benefit", items: psbItems });

  // Continue listening notes (spec: keep capturing agreement / uncertainty / etc)
  sections.push({
    title: "Keep listening for",
    items: [
      "Agreement, disagreement, or hesitation",
      "The prospect's own reasons the solution will work",
      "Objections — surface them early",
    ],
  });

  // Bridge-to-pitch questions from the script.
  sections.push({
    title: "Bridge to pitch",
    items: [
      "\"Did we miss something I should know?\"",
      "\"Do you want to hear my thoughts?\"",
    ],
  });

  return sections;
}

function buildCloseSections({ state, transcript }) {
  const s = state || {};
  const recent = (transcript || []).slice(-12).map((l) => `${l.speaker}: ${l.text}`).join(" ");
  const sections = [];

  // Detect the objection type from recent transcript.
  const objection = OBJECTION_PRICE_RE.test(recent)
    ? "price"
    : OBJECTION_TIME_RE.test(recent)
      ? "time"
      : OBJECTION_SPOUSE_RE.test(recent)
        ? "spouse"
        : OBJECTION_TRUST_RE.test(recent)
          ? "trust"
          : OBJECTION_STALL_RE.test(recent)
            ? "stall"
            : null;

  // Always lead with the script's Close opener — do this BEFORE anything else.
  sections.push({
    title: "Close the deal",
    items: [
      "Confirm timing / readiness to start",
      "Briefly future-pace onboarding",
      "State the investment clearly",
      "SHUT UP — let them respond first",
    ],
  });

  if (objection === "price") {
    const items = ["\"Expensive compared to what?\"", "\"Is it the price, or is it the value?\""];
    const topFinancial = topByMentions(s.financial_situation);
    if (topFinancial) items.push(`They said: "${short(topFinancial.quote || topFinancial.text, 70)}"`);
    const topPain = topByMentions(s.pain_points);
    if (topPain) items.push(`Tie back to pain: "${short(topPain.quote || topPain.text, 70)}"`);
    sections.push({ title: "Price objection", items });
  } else if (objection === "time") {
    const items = ["\"What would make now the right time?\""];
    const topCost = topByMentions(s.cost_of_inaction);
    if (topCost) items.push(`Cost of waiting: "${short(topCost.quote || topCost.text, 70)}"`);
    sections.push({ title: "Timing objection", items });
  } else if (objection === "spouse") {
    sections.push({
      title: "Spouse / partner",
      items: [
        "\"What would they need to hear from you to say yes?\"",
        "Offer a 3-way call rather than \"think about it\"",
      ],
    });
  } else if (objection === "trust") {
    const items = ["\"What specifically didn't work last time?\"", "\"What was missing?\""];
    const topAttempt = topByMentions(s.previous_attempts);
    if (topAttempt) items.push(`They mentioned: "${short(topAttempt.quote || topAttempt.text, 70)}"`);
    sections.push({ title: "Trust objection", items });
  } else if (objection === "stall") {
    sections.push({
      title: "Stall / \"think about it\"",
      items: [
        "\"What specifically do you need to think about?\"",
        "Don't fill the silence — let them answer",
      ],
    });
  }

  // Always surface decision-maker evidence in Close.
  const topDM = topByMentions(s.decision_maker);
  if (topDM) {
    sections.push({
      title: "Decision-maker on file",
      items: [`"${short(topDM.quote || topDM.text, 90)}"`],
    });
  }

  return sections;
}

function buildPitchSections({ state }) {
  const s = state || {};
  const topGoal = topByMentions(s.goals);
  const goalPhrase = topGoal ? short(topGoal.quote || topGoal.text, 60) : "their stated goal";

  const sections = [
    {
      title: "Deliver the pitch",
      items: [
        "Explain the solution briefly, benefit-focused",
        "Involve the prospect — don't monologue",
      ],
    },
    {
      title: "Involve them",
      items: [
        "\"Does that sound like what you're looking for?\"",
        "\"What questions do you have?\"",
        "\"How are you feeling about what we've covered so far?\"",
        `"Are you confident this will help you achieve ${goalPhrase}?"`,
      ],
    },
    {
      title: "If useful: gauge certainty",
      items: [
        "\"On a scale of 1-10, how sure are you this is what you need?\"",
        "If below 10: \"What's missing to get you to 10?\"",
      ],
    },
  ];
  return sections;
}

function buildPostsellSections() {
  return [
    {
      title: "Lock in the win",
      items: [
        "Welcome and congratulate them",
        "Future-pace the next steps",
        "Confirm they will follow the plan",
        "Confirm they will ask for help if they get stuck",
        "Final check that they are fully on board",
      ],
    },
  ];
}

// --- Public API ------------------------------------------------------------
/**
 * Return methodology-aware help sections for the current call state.
 * @param {Object} arg
 * @param {string} arg.phase   Current phase id
 * @param {Object} arg.state   Full dashboard state (pain_points, goals, ...)
 * @param {Array}  arg.transcript Recent transcript lines [{speaker,text,ts}]
 * @param {"b2b"|"b2c"} arg.mode  Discovery orientation
 * @returns {{sub:string, sections: Array<{title:string, items:string[]}>}}
 */
export function getContextualHelpPrompts({ phase, state, transcript, mode = "b2b" }) {
  switch (phase) {
    case "upfront":
      return {
        sub: "Set the frame in the first 2 minutes.",
        sections: buildUpfrontSections(),
      };
    case "discovery":
      return {
        sub: mode === "b2c"
          ? "Get the desired situation first, then the pain."
          : "Get the pain and impact first, then the goals.",
        sections: buildDiscoverySections({ state, mode }),
      };
    case "prescription":
      return {
        sub: "Recap the gap, then frame Problem → Solution → Benefit.",
        sections: buildPrescriptionSections({ state }),
      };
    case "pitch":
      return {
        sub: "Sell the outcome, not the features.",
        sections: buildPitchSections({ state }),
      };
    case "close":
      return {
        sub: "Use their own words. Keep it short.",
        sections: buildCloseSections({ state, transcript }),
      };
    case "postsell":
      return {
        sub: "Lock in the win and prevent buyer's remorse.",
        sections: buildPostsellSections(),
      };
    default:
      return {
        sub: "Stay present. SCH is taking notes.",
        sections: [],
      };
  }
}

/** Flat list of the top short prompts — used by PipView's compact panel. */
export function getFlatHelpPrompts(arg) {
  const { sections } = getContextualHelpPrompts(arg);
  const out = [];
  for (const s of sections) {
    for (const it of s.items) {
      out.push(it);
      if (out.length >= 6) return out;
    }
  }
  return out;
}
